import React from 'react';
import {Map} from 'immutable';
import {
  DATA_ATTR_NODE_TYPE,
  NODE_TYPE_COMMERCE_CART_QUICK_CHECKOUT_ACTIONS,
  QUICK_CHECKOUT_STATE,
  QUICK_CHECKOUT_STATES,
} from '@packages/systems/commerce/constants';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {CommerceCartOrCheckoutFormConstraint} from '@packages/systems/core/constants/SharedConstraintRules';
import {Atom, Plugins, withMeta, withSystem} from '@packages/systems/wfdl';
import {
  pluginName,
  iconName,
  qualify,
  typeName,
} from '@packages/systems/wfdl-Qualified';
import {
  TBoolean,
  TText,
  TListOfElement,
  TRecord,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {useRuntimeEnvironment} from '@packages/systems/render';
import {
  EvaluatedNodeData,
  getBooleanOrElse,
  getChildren,
  getInPojo,
  getStringOrElse,
} from '@packages/utilities/data-utils';

const styleDisplayNone = Map({style: Map({display: 'none'})});

const CommerceCartQuickCheckoutActions = Atom.createRender(
  ({attributes, classes, context, data, generateAttributes, renderChild}) => {
    const defaultState =
      getBooleanOrElse(
        getInPojo(data, ['commerce', 'defaultApplePay']),
        false
      ) || context.get('quickCheckoutEnabled')
        ? QUICK_CHECKOUT_STATES.APPLE_PAY
        : QUICK_CHECKOUT_STATES.NONE;

    const newContext = context.set(
      'previewState',
      getStringOrElse(
        getInPojo(data, ['temp', QUICK_CHECKOUT_STATE]),
        defaultState
      )
    );

    // hide by default in published sites, will be shown when the browser confirms availability
    const runtimeEnvironment = useRuntimeEnvironment();
    const styledAttributes =
      runtimeEnvironment === WebflowEnvironments.SSR
        ? attributes.mergeDeep(styleDisplayNone)
        : attributes;

    return (
      <div {...generateAttributes(styledAttributes, classes)}>
        {getChildren(data).map((child: EvaluatedNodeData) =>
          renderChild(child, newContext)
        )}
      </div>
    );
  }
);

const textStates = [
  {
    label: 'None',
    subtext: '',
    key: QUICK_CHECKOUT_STATES.NONE,
  },
  {
    label: 'Apple Pay',
    subtext: 'Visible on Safari',
    key: QUICK_CHECKOUT_STATES.APPLE_PAY,
  },
  {
    label: 'Pay with browser',
    subtext: 'Visible on Chrome and Edge',
    key: QUICK_CHECKOUT_STATES.PAY_NOW,
  },
];

const config = createIntegratedAtom({
  displayName: 'Web Payments',
  showEmpty: true,
  icon: {
    type: 'Component',
    value: qualify(
      Plugins.name('Commerce'),
      iconName('WebPaymentsSmallElement')
    ),
  },
  constraints: {
    ancestors: [
      // @ts-expect-error - TS2322 - Type '{ readonly is: readonly ["CommerceCartForm", "CommerceCheckoutFormContainer"]; readonly name: "Cart or Checkout Form"; readonly rule: "ExactlyOne"; }' is not assignable to type 'RelationshipConstraint'.
      CommerceCartOrCheckoutFormConstraint,
      {is: 'DynamoWrapper', rule: 'Forbid'},
      {is: 'CommerceCheckoutOrderItemsList', rule: 'Forbid'},
      {is: 'CommerceCheckoutShippingMethodsList', rule: 'Forbid'},
      {is: 'CommerceCartList', rule: 'Forbid'},
    ],
    descendants: [
      {is: 'CommerceCartQuickCheckoutButton', rule: 'ExactlyOne'},
      {is: 'CommerceCartApplePayButton', rule: 'ExactlyOne'},
      {is: 'CommerceCartCheckoutButton', rule: 'Forbid'},
    ],
  },
  render: CommerceCartQuickCheckoutActions,
  showSettingsInDescendants: true,
  fields: {
    children: TListOfElement,
    attr: withSystem(
      {private: false},
      TRecord({
        [DATA_ATTR_NODE_TYPE]: withSystem(
          {
            private: true,
            defaultValue: NODE_TYPE_COMMERCE_CART_QUICK_CHECKOUT_ACTIONS,
          },
          TText
        ),
      })
    ),
    commerce: TRecord({
      type: withSystem(
        {
          defaultValue: 'cart-quick-checkout-actions',
          private: true,
        },
        TText
      ),
      defaultApplePay: withSystem(
        {
          defaultValue: true,
          private: true,
        },
        TBoolean
      ),
    }),
    temp: TRecord({
      state: withMeta(
        {
          textStates,
          hasNote: true,
        },
        withSystem(
          {
            label: 'Preview:',
            labelVariant: 'hidden',
            defaultValue: 'APPLE_PAY',
          },
          TTypeConstructor(
            qualify(pluginName('Commerce'), typeName('WebPaymentsSelect'))
          )
        )
      ),
    }),
  },
});

export default config;
